import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { TransformWrapper, TransformComponent } from "react-zoom-pan-pinch";
import { MdClose, MdFlip } from "react-icons/md";

const MobileZoomModal = ({ isOpen, onClose, front, back, showFlip = true }) => {
  const [isFlipped, setIsFlipped] = useState(false);
  const transformRef = useRef(null);

  // Auto-reset flip state when modal is closed/opened
  useEffect(() => {
    if (!isOpen) {
      setIsFlipped(false);
    }
  }, [isOpen]);

  // Lock body scroll and handle Escape key while modal is open
  useEffect(() => {
    if (!isOpen) return;

    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        onClose?.();
      }
    };
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = originalOverflow;
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const hasBackImage = typeof back === "string" ? back.trim().length > 0 : Boolean(back);
  const canFlip = Boolean(showFlip && hasBackImage);
  const currentImage = isFlipped ? back : front;

  const handleFlip = () => {
    setIsFlipped((prev) => !prev);
    if (transformRef.current) {
      transformRef.current.resetTransform();
    }
  };

  const modalContent = (
    <div className="fixed inset-0 w-screen h-[100dvh] bg-black z-[99999] flex flex-col justify-between overflow-hidden animate-fade-in select-none">
      {/* Top Bar Controls */}
      <div className="flex items-center justify-between px-4 h-14 bg-black/80 backdrop-blur-md z-50 shrink-0 border-b border-white/10">
        <span className="text-white/90 text-xs font-semibold uppercase tracking-wider">
          {canFlip ? (isFlipped ? "Back View" : "Front View") : "Zoom View"}
        </span>
        <button
          onClick={onClose}
          className="text-white hover:text-white/80 p-2 rounded-full bg-white/15 hover:bg-white/25 active:scale-95 transition-all border border-white/20 focus:outline-none cursor-pointer flex items-center justify-center"
          type="button"
          aria-label="Close modal"
        >
          <MdClose size={24} />
        </button>
      </div>

      {/* Main Interactive Zoom Area */}
      <div className="flex-1 w-full h-full flex items-center justify-center relative min-h-0">
        <TransformWrapper
          ref={transformRef}
          initialScale={1}
          initialPositionX={0}
          initialPositionY={0}
          minScale={1}
          maxScale={5}
          centerOnInit
        >
          <TransformComponent
            wrapperClass="w-full h-full"
            contentClass="w-full h-full flex items-center justify-center"
          >
            {currentImage ? (
              <img
                src={currentImage}
                alt={isFlipped ? "Back View" : "Front View"}
                className="max-w-[95vw] max-h-[80vh] object-contain transition-all"
              />
            ) : (
              <div className="text-white/50 text-xs font-semibold">No image available</div>
            )}
          </TransformComponent>
        </TransformWrapper>
      </div>

      {/* Bottom Control Bar */}
      <div className="flex flex-col items-center gap-3 px-4 pb-6 pt-2 bg-gradient-to-t from-black/80 to-transparent z-50 shrink-0">
        {/* Flip Button */}
        {canFlip && (
          <button
            onClick={handleFlip}
            className="flex items-center gap-2 px-5 py-2.5 bg-white text-black rounded-full font-semibold text-xs shadow-xl hover:scale-105 active:scale-95 transition-all focus:outline-none border-none cursor-pointer"
            type="button"
          >
            <MdFlip size={18} />
            <span>Switch to {isFlipped ? "Front" : "Back"}</span>
          </button>
        )}
      </div>
    </div>
  );

  return typeof document !== "undefined"
    ? createPortal(modalContent, document.body)
    : modalContent;
};

export default MobileZoomModal;
